import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { InformeData } from '../types';
import { images } from '../utils/imageBase64';

// Variables globales para tamaños de fuente
const FONT_SIZES = {
  base: 12,           // Fuente base del documento
  h1: 16,             // Títulos principales
  h2: 14,             // Títulos secundarios
  h3: 13,             // Títulos terciarios
  h4: 12,             // Títulos cuaternarios
  paragraph: 11.5,    // Párrafos normales
  paragraphCenter: 12, // Párrafos centrados
  list: 13,           // Items de lista
  field: 10.5,        // Campos editables
  headerTitle: 12.5,  // Título en header
  brand: 14.5,        // Marca CIP
  footer: 9.5,        // Footer
  bibliography: 10,   // Referencias bibliográficas
  link: 10,           // Enlaces
  sectionTitle: 11.5, // Títulos de sección
  tableHeader: 8.5,   // Headers de tabla
  tableCell: 8,       // Categorías tabla
  tableCellContent: 10, // Contenido tabla
  tableTitle: 10.5,   // Título tabla alimentos picantes
  importantTitle: 11.5, // Título recuadro importante
  importantText: 10.5,  // Texto recuadro importante
};

// Estilos para el PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 50, // ~18mm en puntos
    paddingTop: 40,
    paddingBottom: 60,
    fontFamily: 'Helvetica',
    fontSize: FONT_SIZES.base,
  },
  header: {
    flexDirection: 'row',
    marginBottom: 6,
    alignItems: 'flex-start',
  },
  logo: {
    height: 70, // ~25mm para que quepa mejor
    width: 'auto',
    marginRight: 12,
    objectFit: 'contain',
  },
  headerTitle: {
    flex: 1,
    fontSize: FONT_SIZES.headerTitle,
    lineHeight: 1.4,
  },
  brand: {
    fontSize: FONT_SIZES.brand,
    fontWeight: 'bold',
    marginBottom: 1,
  },
  content: {
    flex: 1,
  },
  h1: {
    fontSize: FONT_SIZES.h1,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 5,
    color: '#000',
  },
  h2: {
    fontSize: FONT_SIZES.h2,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 5,
    color: '#000',
  },
  h3: {
    fontSize: FONT_SIZES.h3,
    fontWeight: 'bold',
    marginTop: 0,
    marginBottom: 4,
    color: '#c00',
  },
  h4: {
    fontSize: FONT_SIZES.h4,
    fontWeight: 'bold',
    marginTop: 6,
    marginBottom: 4,
    color: '#000',
  },
  paragraph: {
    fontSize: FONT_SIZES.paragraph,
    lineHeight: 1.4,
    marginBottom: 8,
    textAlign: 'justify',
    color: '#000',
  },
  paragraphCenter: {
    fontSize: FONT_SIZES.paragraphCenter,
    lineHeight: 1.4,
    marginBottom: 10,
    marginTop: 8,
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#c00',
  },
  list: {
    marginVertical: 4,
    paddingLeft: 8,
  },
  listItem: {
    fontSize: FONT_SIZES.list,
    lineHeight: 1.45,
    marginBottom: 4,
  },
  strong: {
    fontWeight: 'bold',
  },
  field: {
    backgroundColor: 'transparent',
    padding: 6,
    marginVertical: 4,
    fontSize: FONT_SIZES.field,
    lineHeight: 1.4,
    minHeight: 25,
  },
  table: {
    width: '100%',
    marginVertical: 4,
    marginTop: 8,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCell: {
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    borderBottomStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#333',
    borderRightStyle: 'solid',
    padding: 1.5,
    fontSize: 6.5,
    lineHeight: 2.4,
    minHeight: 16,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  tableCellFirst: {
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    borderBottomStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#333',
    borderRightStyle: 'solid',
    borderLeftWidth: 1,
    borderLeftColor: '#333',
    borderLeftStyle: 'solid',
    padding: 1.5,
    fontSize: 6.5,
    lineHeight: 2.4,
    minHeight: 16,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  tableCellHeader: {
    borderTopWidth: 1,
    borderTopColor: '#333',
    borderTopStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    borderBottomStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#333',
    borderRightStyle: 'solid',
    padding: 2,
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: 1.1,
    minHeight: 20,
  },
  tableCellHeaderFirst: {
    borderTopWidth: 1,
    borderTopColor: '#333',
    borderTopStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    borderBottomStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: '#333',
    borderRightStyle: 'solid',
    borderLeftWidth: 1,
    borderLeftColor: '#333',
    borderLeftStyle: 'solid',
    padding: 2,
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
    lineHeight: 1.1,
    minHeight: 20,
  },
  greenBg: {
    backgroundColor: '#cfe8cf',
  },
  lightGreenBg: {
    backgroundColor: '#e8efdd',
  },
  pinkBg: {
    backgroundColor: '#f4c6cc',
  },
  image: {
    width: 140, // ~50mm - imagen flotante
    height: 'auto',
    marginBottom: 6,
    marginLeft: 8,
  },
  imageWide: {
    width: 220, // ~80mm - imagen centrada
    height: 'auto',
    marginVertical: 8,
    alignSelf: 'center',
  },
  imageContainer: {
    marginVertical: 4,
    alignItems: 'center',
  },
  imageRight: {
    width: 140, // ~50mm - imagen flotante derecha
    height: 'auto',
    marginBottom: 6,
    marginLeft: 8,
  },
  quoteBox: {
    backgroundColor: '#f7eaea',
    borderLeftWidth: 3,
    borderLeftColor: '#c00',
    borderLeftStyle: 'solid',
    padding: 8,
    marginVertical: 6,
    marginHorizontal: 8,
  },
  bibliographyItem: {
    fontSize: FONT_SIZES.bibliography,
    lineHeight: 1.3,
    marginBottom: 4,
    textAlign: 'justify',
    color: '#000', // Negro para el texto normal
  },
  link: {
    color: '#0000EE', // Azul típico solo para URLs
    fontSize: FONT_SIZES.link,
    textDecoration: 'underline',
  },
  sectionTitle: {
    fontSize: FONT_SIZES.sectionTitle,
    fontWeight: 'bold',
    marginTop: 10,
    marginBottom: 6,
    color: '#333',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    fontSize: FONT_SIZES.footer,
    fontWeight: 'bold',
    color: '#666',
    textAlign: 'left',
  }
});

interface InformePDFProps {
  data: InformeData;
}

const InformePDF: React.FC<InformePDFProps> = ({ data }) => {
  const renderFixedLogo = () => (
    <View style={styles.header} fixed>
      <Image style={styles.logo} src={images.logo} />
    </View>
  );

  const renderHeader = (content?: React.ReactNode, onlyLogo: boolean = false) => (
    <View style={styles.header}>
      <Image style={styles.logo} src={images.logo} />
      {!onlyLogo && (
        <View style={styles.headerTitle}>
          {content || (
            <>
              <Text>
              <Text style={styles.brand}>CIP</Text>
              <Text>es una entidad especializada en ofrecer servicios integrales de cuidado de personas, destacándose por su enfoque personalizado.</Text>
            </Text>
            </>
          )}
        </View>
      )}
    </View>
  );

  const renderFooter = () => (
    <Text style={styles.footer} fixed>
      Medicina preventiva, personalizada y longevidad.
    </Text>
  );

  const getFieldValue = (key: string) => data.campos[key] || '';

  // Función para renderizar el header de la tabla de alimentos
  const renderTableHeader = () => (
    <View style={styles.tableRow} wrap={false} fixed>
      <View style={[styles.tableCellHeaderFirst, {flex: 0.8, backgroundColor: '#fff'}]}>
        <Text style={{fontSize: FONT_SIZES.tableHeader}}>CATEGORÍA</Text>
      </View>
      <View style={[styles.tableCellHeader, {flex: 1, backgroundColor: '#cfe8cf'}]}>
        <Text style={{fontSize: FONT_SIZES.tableHeader}}>RECOMENDADO</Text>
      </View>
      <View style={[styles.tableCellHeader, {flex: 1, backgroundColor: '#e8efdd'}]}>
        <Text style={{fontSize: FONT_SIZES.tableHeader}}>OCASIONALMENTE</Text>
      </View>
      <View style={[styles.tableCellHeader, {flex: 1, backgroundColor: '#f4c6cc'}]}>
        <Text style={{fontSize: FONT_SIZES.tableHeader}}>NO RECOMENDADO</Text>
      </View>
    </View>
  );

  return (
    <Document>
      {/* ========= PÁGINA 1 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            {data.paciente.nombre && (
              <Text style={{fontSize: FONT_SIZES.h4, fontWeight: 'bold', marginBottom: 4}}>
                {data.paciente.nombre}
              </Text>
            )}
            <Text>
            <Text style={styles.brand}>CIP </Text>es una entidad especializada en ofrecer servicios integrales de cuidado de personas, destacándose por su enfoque personalizado.
        </Text>

          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            A través de su unidad <Text style={{fontStyle: 'italic'}}>Cuidado Integral de Personas</Text>, se centra en proporcionar <Text style={{fontWeight: 'bold'}}>calidad de vida</Text> tanto para personas que requieren cuidados especiales con enfermedades ya de base (cáncer, enf. inmunológicas, metabólicas) como para aquellos que buscan mejorar su estilo de vida y longevidad.
          </Text>

          <Text style={styles.paragraphCenter}>
            Lo que ofrecemos es un cambio de estilo de vida.
          </Text>

          <Text style={styles.paragraph}>
            La medicina y el estilo de vida están profundamente interconectados, ya que las decisiones cotidianas que tomamos sobre nuestra salud y bienestar emocional pueden tener un impacto significativo en nuestra calidad de vida y en la prevención de enfermedades y longevidad.
          </Text>

          <Text style={styles.paragraph}>
            Un estilo de vida saludable implica tomar decisiones conscientes sobre <Text style={{fontWeight: 'bold'}}>alimentación</Text>, <Text style={{fontWeight: 'bold'}}>ejercicio</Text>, <Text style={{fontWeight: 'bold'}}>descanso</Text>, <Text style={{fontWeight: 'bold'}}>manejo del estrés</Text> y hábitos sociales "tribu".
          </Text>

          <Text style={styles.h3}>¿Por qué es tan importante el estilo de vida? ¿Cómo nos influye?</Text>

          {/* Sección con imagen a la derecha y texto a la izquierda */}
          <View style={{flexDirection: 'row', marginVertical: 4}}>
            <View style={{flex: 1, paddingRight: 8}}>
            <Text style={styles.paragraph}>
              La respuesta es la <Text style={{fontWeight: 'bold'}}>epigenética</Text>, un campo fascinante de la biología que estudia los cambios en la expresión genética que no implican alteraciones en la secuencia del ADN, pero que pueden influir en cómo se activan o desactivan los genes.
            </Text>

              <Text style={styles.paragraph}>
                Estos cambios epigenéticos son clave para entender cómo factores ambientales —como la alimentación, el estrés, el ejercicio y otros aspectos del estilo de vida— pueden afectar la salud a lo largo de toda la vida y, en algunos casos, incluso ser transmitidos a las siguientes generaciones.
              </Text>
            </View>
            <Image style={styles.imageRight} src={images.imagen_1} />
          </View>

            <Text style={styles.paragraph}>
              En rasgos generales podemos hablar de un <Text style={{fontWeight: 'bold'}}>20% genética</Text> y un <Text style={{fontWeight: 'bold'}}>80% epigenética</Text>: la genética establece la predisposición y la epigenética determina el grado en que esa predisposición se manifiesta.
            </Text>

          <Text style={styles.paragraph}>
            Los factores ambientales, incluidos la dieta, la actividad física, la exposición a toxinas (como el humo del tabaco), el estrés y la falta de sueño, pueden modificar la forma en que se expresan nuestros genes, incluso si no alteran la secuencia genética subyacente. Esto significa que nuestro estilo de vida puede influir en nuestra biología a un nivel muy profundo.
          </Text>
        </View>
      </Page>

      {/* ========= PÁGINA 2 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.paragraph}>
              Muchas formas de cáncer están relacionadas con cambios epigenéticos. Por ejemplo, ciertos genes supresores de tumores pueden ser "apagados" por modificaciones epigenéticas, mientras que otros genes que promueven el crecimiento tumoral pueden ser "activados".
            </Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            Enfermedades como la depresión, la esquizofrenia y el trastorno bipolar se han relacionado con alteraciones epigenéticas, especialmente en cómo los genes responden a factores estresantes y a los cambios en el ambiente. Las modificaciones epigenéticas pueden influir en el metabolismo y en la inflamación, contribuyendo al desarrollo de condiciones como la diabetes tipo 2, la hipertensión y la aterosclerosis; así como no olvidar el sistema inmunológico, que responde a los propios tejidos del cuerpo, lo que puede contribuir al desarrollo de enfermedades autoinmunes.
          </Text>

          <View style={styles.imageContainer}>
            <Image style={styles.imageWide} src={images.imagen_2} />
          </View>

          <Text style={styles.paragraphCenter}>
            La epigenética subraya la increíble capacidad que tenemos para "modificar nuestra biología a través de nuestras elecciones diarias".
          </Text>

          <Text style={styles.paragraph}>
            Un enfoque multidisciplinar permite abordar enfermedades complejas que requieren la intervención de diferentes especialistas para obtener una solución completa, aunando la medicina convencional con estrategias complementarias.
          </Text>

          <Text style={styles.paragraph}>
            CIP está formado por un conjunto de especialistas sanitarios: médicos especialistas en oncología médica, medicina interna y medicina de familia, fisioterapeutas, psicología y terapia, preparadores físicos, PNI y podólogos, entre otros. Esta visión integradora nos permite diseñar planes de cuidado individualizados y basados en la evidencia.
          </Text>

          <Text style={styles.paragraph}>
            Buscamos tratar al paciente de manera integral, considerando no solo los síntomas físicos, sino también sus necesidades emocionales, sociales y psicológicas. De esta forma, el plan de intervención acompaña al paciente y a su entorno, promoviendo hábitos sostenibles que impacten positivamente en su salud y calidad de vida.
          </Text>

          <Text style={styles.paragraph}>
            Cuando un paciente ingiere varios medicamentos, nuestro personal médico analiza con un software especializado las posibles interacciones entre dichos medicamentos u otras sustancias de consumo habitual —como pueden ser infusiones, especias o alimentos—. El objetivo de este estudio es prevenir efectos secundarios indeseados y optimizar la función de los medicamentos para que sean tan eficaces y seguros como sea posible.
          </Text>
        </View>
      </Page>

      {/* ========= PÁGINA 3 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={{...styles.paragraph, fontWeight: 'bold'}}>
              Tus hábitos de vida te han llevado a estar en la situación actual, por lo que un cambio de estos hábitos será la ruta a la mejoría.
            </Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            Este cambio de estilo de vida puede conllevar también un cambio de hábito alimenticio. Una dieta implica un día de inicio y un día de fin. ¿Quieres estar saludable durante unos meses, o prefieres estar saludable toda la vida?
          </Text>

          <View style={styles.quoteBox}>
            <Text style={{fontWeight: 'bold', marginBottom: 2, textAlign: 'center'}}>
              No podemos pretender que las cosas cambien si seguimos haciendo lo mismo.
            </Text>
            <Text style={{fontWeight: 'normal', textAlign: 'center'}}>
              (Albert Einstein, físico)
            </Text>
          </View>

          <Text style={styles.paragraph}>
            Por estos motivos CIP te recomendamos encarecidamente que tengas una alimentación extremadamente variada y no pases por alto nuestras recomendaciones.
          </Text>

          <Text style={styles.paragraph}>
            Es por eso que los alimentos catalogados como <Text style={{fontWeight: 'bold'}}>no recomendados</Text> son alimentos que deberías evitar no sólo mientras te encuentres en fase de recuperación, sino como medida preventiva. De la misma manera los alimentos que recomendamos consumir frecuentemente deberías de incorporarlos a tu alimentación incluso una vez superada la fase de recuperación, ya que igual de perjudicial es alimentarse con algo potencialmente tóxico o dañino, que dejar de recibir nutrientes o micronutrientes. Las dos situaciones generan enfermedad. La clave de la salud es, entre otros motivos, consumir cuanta más diversidad de alimentos saludables mejor.
          </Text>

          <Text style={{...styles.paragraph, color: '#c00', fontWeight: 'bold', marginTop: 6}}>
            ESTRATEGIAS DE TRATAMIENTOS BASADAS EN EVIDENCIA CIENTÍFICA:
          </Text>

          <Text style={styles.paragraph}>
            Las estrategias que te recomendamos para tu tratamiento te pueden parecer exageradas y que van en contra de nuestra costumbre de comer, pero su utilidad terapéutica está más que demostrada científicamente. Su enfoque no es restablecer un ritmo fisiológico, generar un cambio metabólico e inmunológico y estimular a un área del cerebro que se denomina hipotálamo, que se encarga del grado de mantener estables casi todos los parámetros importantes para mantener una salud óptima.
          </Text>

          <Text style={styles.paragraph}>
            Esto lo conseguimos con diferentes estrategias: alimentación, medicación, actividad física, hábitos de vida, ejercicios respiratorios, ejercicios de temperatura… Posiblemente te recomendemos cambios que te resultarán "raros" para todavía la mayoría de la sociedad de hoy en día, pero todos ellos estarán justificados científicamente. <Text style={{fontWeight: 'bold'}}>CONFÍA.</Text>
          </Text>

          <Text style={{...styles.paragraph, textAlign: 'center', marginTop: 6}}>
            Te la detallamos a continuación.
          </Text>
        </View>
      </Page>

      {/* ========= PÁGINA 4 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.h3}>FARMACOLOGÍA Y SUPLEMENTACIÓN:</Text>
            <Text style={styles.paragraph}>
              Los suplementos que te indicamos a continuación son productos avalados científicamente, no interaccionan con ningún tipo de fármaco que estés tomando. Igualmente no generan adicción, pretenden restablecer algunos nutrientes, ayudar a mitigar los efectos secundarios de los tratamientos farmacológicos sin restarle eficacia, aumentar la modulación de nuestro sistema inmune o simplemente intentar o ayudar a frenar directamente tu enfermedad.
            </Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            Los puedes encontrar o encargar en cualquier parafarmacia y herbolario especializado. Los fármacos indicados en el tratamiento están indicados para tu enfermedad y no interaccionan con otros que ya estés usando.
          </Text>

          {getFieldValue('farmacologia_y_suplementacion') && (
            <View style={styles.field}>
              <Text>{getFieldValue('farmacologia_y_suplementacion')}</Text>
            </View>
          )}
        </View>
      </Page>

      {/* ========= PÁGINA 5 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.h3}>ESTRATEGIAS PARA EL ESTILO DE VIDA:</Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <View style={{marginBottom: 4}}>
            <Text style={{fontWeight: 'bold', marginBottom: 2}}>RUTINA Y CICLO CIRCADIANO:</Text>
            {getFieldValue('p6_rutina_circadiano') && (
              <View style={styles.field}>
                <Text>{getFieldValue('p6_rutina_circadiano')}</Text>
              </View>
            )}
          </View>

          <View style={{marginBottom: 4}}>
            <Text style={{fontWeight: 'bold', marginBottom: 2}}>ACTIVIDAD Y DEPORTE:</Text>
            {getFieldValue('p6_actividad_deporte') && (
              <View style={styles.field}>
                <Text>{getFieldValue('p6_actividad_deporte')}</Text>
              </View>
            )}
          </View>

          <View style={{marginBottom: 4}}>
            <Text style={{fontWeight: 'bold', marginBottom: 2}}>ESTRÉS:</Text>
            {getFieldValue('p6_estres') && (
              <View style={styles.field}>
                <Text>{getFieldValue('p6_estres')}</Text>
              </View>
            )}
          </View>

          <View style={{marginBottom: 4}}>
            <Text style={{fontWeight: 'bold', marginBottom: 2}}>MODO DE VIDA (AYUNO, FRÍO, MEDITACIÓN, HIPOXIA, HIPERCAPNIA, SAUNA, HIGIENE BUCODENTAL, HIDRATACIÓN, ELIMINACIÓN DE TÓXICOS…):</Text>
            {getFieldValue('p6_modo_de_vida') && (
              <View style={styles.field}>
                <Text>{getFieldValue('p6_modo_de_vida')}</Text>
              </View>
            )}
          </View>
        </View>
      </Page>

      {/* ========= PÁGINA 6 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.h3}>RECOMENDACIONES ALIMENTARIAS:</Text>
            <Text style={styles.paragraph}>
              Antes de detallar tus especificaciones alimenticias, es importante que sepas algo muy importante sobre dos grandes grupos.
            </Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.paragraph}>
            El primero son los <Text style={{fontWeight: 'bold'}}>alimentos ultraprocesados</Text>, están asociados con un alto riesgo de desarrollar enfermedades crónicas. A pesar de su alta densidad calórica, los ultraprocesados suelen carecer de nutrientes esenciales como vitaminas, minerales y fibra, lo que puede contribuir a deficiencias nutricionales.
          </Text>

          <Text style={styles.paragraph}>
            El segundo son los <Text style={{fontWeight: 'bold'}}>azúcares</Text>. Existen dos tipos: los que vienen naturalmente en alimentos reales (frutas, tubérculos…) y los <Text style={{fontWeight: 'bold'}}>azúcares añadidos</Text>. Estos azúcares añadidos son realmente perjudiciales para la salud e innecesarios para nuestra supervivencia. Evitarlos en la alimentación mejorará muchos factores de tu salud y prevendrá muchas enfermedades.
          </Text>

          {/* Tabla de alimentos con header que se repite */}
          {renderTableHeader()}

          {/* Filas de la tabla */}
          {[
            {label: 'PESCADOS', key: 'pescados'},
            {label: 'CARNES', key: 'carnes'},
            {label: 'VERDURAS Y HORTALIZAS', key: 'verduras'},
            {label: 'FRUTAS', key: 'frutas'},
            {label: 'SETAS', key: 'setas'},
            {label: 'LEGUMBRES', key: 'legumbres'},
            {label: 'CEREALES', key: 'cereales'},
            {label: 'TUBÉRCULOS', key: 'tuberculos'},
            {label: 'BEBIDAS VEGETALES', key: 'bebidas'},
            {label: 'PRODUCTOS LÁCTEOS', key: 'lacteos'},
            {label: 'MARISCOS', key: 'mariscos'},
            {label: 'EMBUTIDOS', key: 'embutidos'},
            {label: 'HUEVOS', key: 'huevos'},
            {label: 'DULCES', key: 'dulces'},
            {label: 'FRUTOS SECOS', key: 'frutos'},
            {label: 'AGUA', key: 'agua'},
            {label: 'CAFÉ, INFUSIONES', key: 'cafe'},
            {label: 'ENCURTIDOS', key: 'encurtidos'},
            {label: 'ALGAS', key: 'algas'},
            {label: 'SAL Y ESPECIAS', key: 'sal'}
          ].map((categoria, idx) => (
            <View key={idx} style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellFirst, {flex: 0.8}]}>
                <Text style={{fontSize: FONT_SIZES.tableCell, fontWeight: 'bold'}}>{categoria.label}</Text>
              </View>
              <View style={[styles.tableCell, {flex: 1, backgroundColor: '#cfe8cf'}]}>
                <Text style={{fontSize: FONT_SIZES.tableCellContent}}>{getFieldValue(`p6_${categoria.key}_rec`) || ' '}</Text>
              </View>
              <View style={[styles.tableCell, {flex: 1, backgroundColor: '#e8efdd'}]}>
                <Text style={{fontSize: FONT_SIZES.tableCellContent}}>{getFieldValue(`p6_${categoria.key}_ocas`) || ' '}</Text>
              </View>
              <View style={[styles.tableCell, {flex: 1, backgroundColor: '#f4c6cc'}]}>
                <Text style={{fontSize: FONT_SIZES.tableCellContent}}>{getFieldValue(`p6_${categoria.key}_no`) || ' '}</Text>
              </View>
            </View>
          ))}

          {/* Campo ALIMENTOS PICANTES */}
          <Text style={{fontSize: FONT_SIZES.tableTitle, fontWeight: 'bold', marginTop: 8, marginBottom: 3}}>ALIMENTOS PICANTES:</Text>
          {getFieldValue('p6_alimentos_picantes') ? (
            <View style={styles.field}>
              <Text>{getFieldValue('p6_alimentos_picantes')}</Text>
            </View>
          ) : (
            <View style={{...styles.field, minHeight: 40}}>
              <Text style={{color: '#999'}}> </Text>
            </View>
          )}
        </View>
      </Page>

      {/* ========= PÁGINA 7 ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.h3}>PLANIFICACIÓN DE PROCEDIMIENTOS:</Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          {getFieldValue('p7_planificacion_procedimientos') && (
            <View style={styles.field}>
              <Text>{getFieldValue('p7_planificacion_procedimientos')}</Text>
            </View>
          )}

          <View style={{
            marginTop: 10,
            padding: 10,
            borderWidth: 1,
            borderColor: '#e0c5a5',
            borderStyle: 'solid',
            backgroundColor: '#fff6ee',
            borderRadius: 8,
            width: '80%',
            alignSelf: 'center',
          }}>
            <Text style={{fontSize: FONT_SIZES.importantTitle, fontWeight: 'bold', color: '#c00', textAlign: 'center', marginBottom: 4}}>
              IMPORTANTE
            </Text>
            <Text style={{fontSize: FONT_SIZES.importantText, color: '#c00', textAlign: 'center', lineHeight: 1.4}}>
              Es muy frecuente que, cuando se comienza a establecer nuevos hábitos de vida, aparezcan durante unos días algunos síntomas como cefaleas, cansancio o dolores. No te alarmes. Esto no debe constituir un motivo para abandonar nuestras recomendaciones. Estos síntomas no suelen persistir más de 7–10 días.
            </Text>
          </View>
        </View>
      </Page>

      {/* ========= PÁGINA 8 - Bibliografía ========= */}
      <Page size="A4" style={styles.page}>
        <View style={{flexDirection: 'row', marginBottom: 8}} fixed>
          <Image style={styles.logo} src={images.logo} />
        </View>
        <View style={{...styles.header, marginTop: -90}}>
          <Image style={{...styles.logo, opacity: 0}} src={images.logo} />
          <View style={styles.headerTitle}>
            <Text style={styles.h3}>Bibliografía que justifica nuestros tratamientos</Text>
          </View>
        </View>
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.h4}>1. Dieta y evitación de procesados</Text>
          <Text style={styles.bibliographyItem}>
            • Dai, S., Liu, D., Wang, H., et al. (2024). Ultra-processed foods and human health: An umbrella review of meta-analyses. Clinical Nutrition, 43(3), 602–613. <Text style={styles.link}>https://doi.org/10.1016/j.clnu.2024.03.027</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Lane, M. M., Davis, J. A., Beattie, S., et al. (2024). Ultra-processed food exposure and adverse health outcomes: umbrella review of epidemiological meta-analyses. BMJ, 384, e077310. <Text style={styles.link}>https://doi.org/10.1136/bmj-2023-077310</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Vitale, M., Sparano, C., La Vecchia, C., et al. (2024). Ultra-Processed Foods and Human Health: A Systematic Review and Meta-Analysis of Prospective Cohort Studies. Nutrition and Health, 30(1), 56–84. <Text style={styles.link}>https://doi.org/10.1177/02601060231223313</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Hall, K. D., Ayuketah, A., Brychta, R., et al. (2019). Ultra-processed diets cause excess calorie intake and weight gain: an inpatient randomized controlled trial of ad libitum food intake. Cell Metabolism, 30(1), 67–77. <Text style={styles.link}>https://doi.org/10.1016/j.cmet.2019.05.008</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Gomes Gonçalves Ferreira, L., da Costa Louzada, M. L., Steele, E. M., et al. (2020). Consumption of ultra-processed foods and health status: a systematic review and meta-analysis. Nutrition Reviews, 78(11), 1059–1074. <Text style={styles.link}>https://doi.org/10.1093/nutrit/nuaa038</Text>
          </Text>

          <Text style={{...styles.h4, marginTop: 8}}>2. Ejercicio físico multimodal como prevención y tratamiento</Text>
          <Text style={styles.bibliographyItem}>
            • Feito, Y., Heinrich, K. M., Butcher, S. J., & Poston, W. S. C. (2018). High-Intensity Multimodal Training (HIMT) for fitness promotion. Frontiers in Physiology, 12, 723486. <Text style={styles.link}>https://doi.org/10.3389/fphys.2021.723486</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Meneses-Echávez, J. F., González-Jiménez, E., & Ramírez-Vélez, R. (2015). Effects of supervised multimodal exercise interventions on cancer-related fatigue: systematic review and meta-analysis of randomized controlled trials. Asian Pacific Journal of Cancer Prevention, 16(13), 4713–4719. <Text style={styles.link}>https://doi.org/10.7314/APJCP.2015.16.13.4713</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Carnes, J., & Mahoney, T. (2019). The effect of multimodal exercise interventions on older adults: a systematic review. Journal of Aging and Physical Activity, 27(5), 679–690. <Text style={styles.link}>https://doi.org/10.1123/japa.2018-0159</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • World Health Organization. (2020). Guidelines on physical activity and sedentary behaviour. Geneva: WHO. <Text style={styles.link}>https://www.who.int/publications/i/item/9789240015128</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Claudino, J. G., Gabbett, T. J., Bourgeois, F., et al. (2018). CrossFit overview: Systematic review and meta-analysis. Sports Medicine – Open, 4(1), 1–14. <Text style={styles.link}>https://doi.org/10.1186/s40798-018-0124-5</Text>
          </Text>
        </View>
      </Page>

      {/* ========= PÁGINA 9 ========= */}
      <Page size="A4" style={styles.page}>
        {renderFixedLogo()}
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.h4}>3. Calidad de sueño y optimización no farmacológica</Text>
          <Text style={styles.bibliographyItem}>
            • Wang, Y., Xu, B., Wang, X., et al. (2024). Efficacy of non-pharmacological interventions for sleep quality in adults: a network meta-analysis. Frontiers in Neuroscience, 18. <Text style={styles.link}>https://doi.org/10.3389/fnins.2024.1337619</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Wu, Y., Tang, Q., Wang, X., et al. (2023). The effectiveness of non-pharmacological sleep interventions for improving inpatient sleep quality: a systematic review and meta-analysis. Sleep Medicine Reviews, 68, 101801. <Text style={styles.link}>https://doi.org/10.1016/j.smrv.2023.101801</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Harvey, A. G., & Buysse, D. J. (2018). Non-pharmacological strategies for improving sleep: mindfulness, behavioral therapy and sleep hygiene. Sleep Medicine Reviews, 25, 23–36. <Text style={styles.link}>https://doi.org/10.1016/j.smrv.2015.11.003</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Morin, C. M., & Benca, R. (2012). Insomnia. The Lancet, 379(9821), 1129–1141. <Text style={styles.link}>https://doi.org/10.1016/S0140-6736(11)60750-2</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Chong, Y., & Cheung, J. M. (2017). Cognitive behavioral therapy for insomnia: a meta-analysis of randomized controlled trials. Journal of the American Medical Association, 325(1), 21–35.
          </Text>

          <Text style={{...styles.h4, marginTop: 8}}>4. Suplementación básica para personas de edad media en Europa</Text>
          <Text style={styles.bibliographyItem}>
            • Zembura, P., Muntasewicz, K., Metelska, P., et al. (2023). Is dietary (food) supplement intake reported in European national nutrition surveys? Nutrients, 15(1), 236. <Text style={styles.link}>https://doi.org/10.3390/nu15010236</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Pop, M. S., Pop, L. C., & Năstase, M. S. (2023). Exploring the potential benefits of natural calcium-rich mineral waters: a systematic review. Nutrients, 15(7), 1587. <Text style={styles.link}>https://doi.org/10.3390/nu15071587</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Kennedy, D. O. (2016). B vitamins and the brain: mechanisms, dose and efficacy—review. Nutrients, 8(2), 68. <Text style={styles.link}>https://doi.org/10.3390/nu8020068</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • EFSA Panel on Nutrition, Novel Foods and Food Allergens (NDA) et al. (2016). Scientific opinion on nutrient requirements and dietary recommendations for older adults in the European Union. EFSA Journal, 14(2), 4300. <Text style={styles.link}>https://doi.org/10.2903/j.efsa.2016.4300</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Cruz-Jentoft, A. J., Landi, F., Topinková, E., & Michel, J. P. (2008). Understanding sarcopenia as a geriatric syndrome. Current Opinion in Clinical Nutrition & Metabolic Care, 13(1), 1–7.
          </Text>
        </View>
      </Page>

      {/* ========= PÁGINA 10 ========= */}
      <Page size="A4" style={styles.page}>
        {renderFixedLogo()}
        {renderFooter()}
        <View style={styles.content}>
          <Text style={styles.h4}>5. Ingesta de agua y minerales</Text>
          <Text style={styles.bibliographyItem}>
            • Pop, M. S., Pop, L. C., & Năstasă, M. S. (2023). Exploring the potential benefits of natural calcium-rich mineral waters: a systematic review. Nutrients, 15(7), 1587. <Text style={styles.link}>https://doi.org/10.3390/nu15071587</Text>
          </Text>
          <Text style={styles.bibliographyItem}>
            • Healthline. (2019). Does mineral water have health benefits? Human studies and evidence summarized.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Ferraro, P. M., Curhan, G. C., Gambaro, G., & Taylor, E. N. (2015). Intake of water and beverages and the risk of kidney stones in men and women. The American Journal of Clinical Nutrition, 102(6), 1565–1575.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Sabatier, M., Hariton, F., Darmon, P., et al. (2017). The bioavailability of calcium from fortified mineral water: A systematic review. European Journal of Clinical Nutrition, 71(3), 275–282.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Vannucci, L., Fossi, C., Quattrini, S., et al. (2018). Calcium intake in bone health: A focus on mineral waters. Nutrients, 10(12), 1390. <Text style={styles.link}>https://doi.org/10.3390/nu10121390</Text>
          </Text>

          <Text style={{...styles.h4, marginTop: 8}}>6. Regulación SNA, cortisol y sistema neuroendocrino</Text>
          <Text style={styles.bibliographyItem}>
            • Zaccaro, A., et al. (2018). How breath-control can change your life: A systematic review on psycho-physiological correlates of slow breathing. Frontiers in Human Neuroscience, 12, 353.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Noble, D. J., & Hochman, S. (2019). Hypothesis: Pulmonary afferent activity patterns during slow, deep breathing contribute to the neural induction of physiological relaxation. Frontiers in Physiology, 10, 1176.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Busch, V., et al. (2012). The effect of deep and slow breathing on pain perception, autonomic activity, and mood processing. Pain Medicine, 13(2), 215–228.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Almahqamh, O., & Hammond, L. (2024). Does the Wim Hof Method have a beneficial impact on physiological and psychological outcomes? A systematic review. PLOS ONE, 19(3), e0286393.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Kujawski, S., et al. (2022). Combination of whole-body cryotherapy with static stretching exercises reduces fatigue and improves functioning of the autonomic nervous system in Chronic Fatigue Syndrome. Journal of Translational Medicine, 20, 273.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Louis, J., et al. (2015). Head exposure to cold during whole-body cryostimulation: Influence on thermal response and autonomic modulation. PLOS ONE, 10(4), e0124776.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Schaal, K., et al. (2013). Parasympathetic activity and blood catecholamine responses following a single partial-body cryostimulation and a whole-body cryostimulation. PLOS ONE, 8(8), e72658.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Laukkanen, J. A., et al. (2019). Recovery from sauna bathing favorably modulates cardiac autonomic nervous system. European Journal of Preventive Cardiology, 26(15), 1636–1642.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Hussain, J., & Cohen, M. (2018). Clinical effects of regular dry sauna bathing: A systematic review. Evidence-Based Complementary and Alternative Medicine, 2018, 1857413.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Optimización de vagal stimulation protocol based on spontaneous breathing rate. Frontiers in Physiology (2018).
          </Text>
          <Text style={styles.bibliographyItem}>
            • Acute effects of the 4–4–8 breathing technique on arterial stiffness in healthy young men. Scientific Reports (2024).
          </Text>
          <Text style={styles.bibliographyItem}>
            • Ramos-Campo, D. J., et al. (2024). Cryostimulation and/or cold-water immersion exposure enhance parasympathetic nervous activity: A systematic review and meta-analysis. Physiology & Behavior, 274, 114528.
          </Text>
          <Text style={styles.bibliographyItem}>
            • Costa-Silva, et al. (2019). Acute effects of cold water immersion on cardiovascular and autonomic responses. RIAA, 3(5), 8–13.
          </Text>
          <Text style={styles.bibliographyItem}>
            • The effects of cold water immersion with different dosages on heart rate variability indices. Science & Sports (2016).
          </Text>
          <Text style={styles.bibliographyItem}>
            • Brennan, V., et al. (2024). Whole-body cold stimulation improves cardiac autonomic control independent of the employed temperature. Journal of Clinical Medicine, 13(24), 7728.
          </Text>
          <Text style={styles.bibliographyItem}>
            • An alternating breathing pattern significantly affects the brain functional connectivity and mood states. Frontiers in Human Neuroscience (2025).
          </Text>
          <Text style={styles.bibliographyItem}>
            • Vagus activation by Cold Face Test reduces acute psychosocial stress responses. PLOS ONE (2022).
          </Text>
          <Text style={styles.bibliographyItem}>
            • The use of whole-body cryotherapy: time- and dose-response investigation on circulating blood catecholamines and heart rate variability. European Journal of Applied Physiology (2020).
          </Text>
          <Text style={styles.bibliographyItem}>
            • Effects of cryotherapy methods on circulatory, metabolic, inflammatory and neural properties: A systematic review. Fisioterapia em Movimento (2016).
          </Text>
          <Text style={styles.bibliographyItem}>
            • The Effects of Heat and Massage Application on Autonomic Nervous System. Annals of Rehabilitation Medicine (2011).
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default InformePDF;
